import { Channel, invoke } from '@tauri-apps/api/core'
import { isMacOSDesktop, isTauriRuntime } from '~/lib/platform/runtime'
import workletUrl from './nativeMicWorklet.js?url'

// macOS: the microphone is captured by Rust (`commands/native_mic.rs`), not by `getUserMedia`.
//
// WebKit's own capture makes its GPU process reconfigure the current output device, and CoreAudio
// restarts that device's IO for every app at once: music in Bluetooth headphones or the built-in
// speakers dropped out for a second or two whenever the mic came on in a call. Capture from our own
// process leaves the output device alone (checked against coreaudiod/bluetoothd logs).
//
//   Rust (cpal) --16-bit mono PCM, ~20 ms chunks, Tauri Channel--> NativeMicSource
//     -> AudioWorkletNode (nativeMicWorklet.js: resample + jitter buffer)
//     -> MediaStreamAudioDestinationNode -> MediaStreamTrack -> published by LiveKitManager
//
// The track stays the same for the source's whole life — switching devices only swaps the Rust
// capture behind it — so RNNoise, the mic gain chain and the RTP sender never see it change.
// No echo cancellation on this path: plain CoreAudio has none, and WebKit's is what ducked other
// apps (see `appSettings.ts`).
//
// Every other platform (Windows, Linux, iOS, Android, a plain browser tab) keeps `getUserMedia`.

type NativeMicDevice = { name: string; is_default: boolean }
type NativeMicStarted = { capture_id: number; sample_rate: number }

export function isNativeMicAvailable(): boolean {
  return isTauriRuntime() && isMacOSDesktop()
}

/**
 * Input devices as `MediaDeviceInfo`-shaped rows, so the existing pickers and
 * `devicePreferences.ts` work unchanged. `deviceId` is the CoreAudio device name — what
 * `native_mic_start` matches against.
 */
export async function listNativeMicrophones(): Promise<MediaDeviceInfo[]> {
  return toDeviceInfos(await invoke<NativeMicDevice[]>('native_mic_list_devices'), 'audioinput')
}

/**
 * Output devices from CoreAudio (`commands/native_speaker.rs`): WebKit never lists them here,
 * since the page has no `getUserMedia` grant. Same shape and id scheme as `listNativeMicrophones`.
 */
export async function listNativeSpeakers(): Promise<MediaDeviceInfo[]> {
  return toDeviceInfos(await invoke<NativeMicDevice[]>('native_speaker_list_devices'), 'audiooutput')
}

function toDeviceInfos(devices: NativeMicDevice[], kind: MediaDeviceKind): MediaDeviceInfo[] {
  return devices.map(device => ({
    deviceId: device.name,
    groupId: '',
    kind,
    label: device.name,
    toJSON() { return { deviceId: device.name, groupId: '', kind, label: device.name } },
  }))
}

const workletLoaders = new WeakMap<AudioContext, Promise<void>>()

function loadWorklet(context: AudioContext) {
  let loader = workletLoaders.get(context)
  if (!loader) {
    loader = context.audioWorklet.addModule(workletUrl)
    loader.catch(() => workletLoaders.delete(context))
    workletLoaders.set(context, loader)
  }
  return loader
}

type Capture = { id: number; channel: Channel<ArrayBuffer> }

async function startCapture(deviceId: string, node: AudioWorkletNode): Promise<Capture> {
  const channel = new Channel<ArrayBuffer>()
  // Chunks can land before `native_mic_start` resolves with the device's rate — the first ~20 ms
  // are dropped rather than resampled from a guessed rate.
  let rate: number | null = null
  channel.onmessage = (pcm) => {
    if (rate !== null) node.port.postMessage({ pcm, rate }, [pcm])
  }
  const started = await invoke<NativeMicStarted>('native_mic_start', {
    deviceName: deviceId === 'default' ? null : deviceId,
    onAudio: channel,
  })
  rate = started.sample_rate
  return { id: started.capture_id, channel }
}

function stopCapture(capture: Capture) {
  capture.channel.onmessage = () => undefined
  return invoke('native_mic_stop', { captureId: capture.id }).catch(() => undefined)
}

export class NativeMicSource {
  private stopped = false

  private constructor(
    readonly track: MediaStreamTrack,
    private readonly node: AudioWorkletNode,
    private readonly destination: MediaStreamAudioDestinationNode,
    private capture: Capture,
  ) {}

  /** `context` must be running — the worklet only renders (and the track only carries audio) while it does. */
  static async start(context: AudioContext, deviceId: string): Promise<NativeMicSource> {
    await loadWorklet(context)
    const node = new AudioWorkletNode(context, 'native-mic-source', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    })
    const destination = context.createMediaStreamDestination()
    node.connect(destination)
    const track = destination.stream.getAudioTracks()[0]
    try {
      if (!track) throw new Error('Не удалось создать трек микрофона')
      return new NativeMicSource(track, node, destination, await startCapture(deviceId, node))
    } catch (error) {
      node.disconnect()
      track?.stop()
      throw error
    }
  }

  /** Starts the new device before stopping the old one, so the track never goes silent in between. */
  async switchDevice(deviceId: string) {
    if (this.stopped) return
    const next = await startCapture(deviceId, this.node)
    if (this.stopped) {
      await stopCapture(next)
      return
    }
    const previous = this.capture
    this.capture = next
    previous.channel.onmessage = () => undefined
    this.node.port.postMessage({ type: 'reset' })
    await stopCapture(previous)
  }

  /** Idempotent. */
  async stop() {
    if (this.stopped) return
    this.stopped = true
    try { this.node.disconnect() } catch { /* already disconnected */ }
    try { this.destination.disconnect() } catch { /* already disconnected */ }
    this.track.stop()
    await stopCapture(this.capture)
  }
}
